import { useState, useEffect } from "react";
import "@fontsource/montserrat";
import {
  Paper,
  Stack,
  Group,
  NumberInput,
  Button,
  FileInput,
  Text,
  Modal,
  Code,
  ActionIcon,
  Box,
  ColorInput,
  Select,
  Badge,
  Alert,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconInfoCircle,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { cropImage } from "../components/ImageCropModal";
import { PX_PER_MM } from "../components/builder/constants";
import { useCanvasEditor } from "../components/builder/useCanvasEditor";
import { useBlobUrlCleanup } from "../components/builder/useBlobUrlCleanup";
import ResizeHandle from "../components/builder/ResizeHandle";

export default function PDFBuilder({
  availablePlaceholders = [
    { value: "fullName", label: "Full name" },
    { value: "firstName", label: "First name" },
    { value: "lastName", label: "Last name" },
    { value: "date", label: "Date" },
    { value: "eventName", label: "Event name" },
  ],
  initialValue,
  onChange,
  onSubmit,
  onCancel,
}) {
  const [docFormat, setDocFormat] = useState(
    initialValue?.docFormat || { widthMm: 210, heightMm: 297 }
  );
  const [bgImage, setBgImage] = useState(initialValue?.bgImage || null);
  const [bgFile, setBgFile] = useState(null);
  const [placeholders, setPlaceholders] = useState(
    initialValue?.placeholders || []
  );
  const [newPlaceholder, setNewPlaceholder] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [bgError, setBgError] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);

  const nativeWidth = docFormat.widthMm * PX_PER_MM;
  const nativeHeight = docFormat.heightMm * PX_PER_MM;

  const {
    containerRef,
    paneRef,
    scale,
    draggingId,
    handlePointerDown,
    handleResizePointerDown,
    handlePointerMove,
    handlePointerUp,
  } = useCanvasEditor({
    items: placeholders,
    setItems: setPlaceholders,
    nativeWidth,
    nativeHeight,
  });

  useEffect(() => {
    if (onChange) {
      onChange({ docFormat, bgImage, placeholders });
    }
  }, [docFormat, bgImage, placeholders, onChange]);

  const handleCancel = () => {
    setDocFormat(initialValue?.docFormat || { widthMm: 210, heightMm: 297 });
    setBgImage(initialValue?.bgImage || null);
    setPlaceholders(initialValue?.placeholders || []);
    if (onCancel) onCancel();
  };

  const handleBgUpload = async (file) => {
    setBgError(null);
    if (file) {
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        setBgError("Only PNG and JPG images are allowed.");
        setBgImage(null);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setBgError("Image size must not exceed 5MB.");
        setBgImage(null);
        return;
      }
      const aspectRatio = docFormat.widthMm / docFormat.heightMm;
      const croppedBlob = await cropImage(file, aspectRatio);
      if (!croppedBlob) return;
      const croppedFile = new File([croppedBlob], file.name, {
        type: croppedBlob.type,
      });
      const previewUrl = URL.createObjectURL(croppedFile);
      setBgImage(previewUrl);
      setBgFile(croppedFile);
    } else {
      setBgImage(null);
      setBgFile(null);
    }
  };

  useBlobUrlCleanup(bgImage);

  const handleAddPlaceholder = () => {
    if (!newPlaceholder.trim()) return;
    const newId = Date.now().toString();
    setPlaceholders((prev) => [
      ...prev,
      {
        id: newId,
        name: newPlaceholder.trim(),
        xPct: 10,
        yPct: 10,
        widthPct: 30,
        heightPct: 5,
        fontSize: 16,
        color: "#000000",
        align: "left",
      },
    ]);
    setSelectedId(newId);
    setNewPlaceholder("");
  };

  const removePlaceholder = (id, e) => {
    if (e) e.stopPropagation();
    setPlaceholders((prev) => prev.filter((p) => p.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const updatePlaceholder = (id, key, value) => {
    setPlaceholders((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [key]: value } : p))
    );
  };

  const onPlaceholderPointerDown = (e, id, xPct, yPct) => {
    if (e.target.closest(".resize-handle")) return;
    setSelectedId(id);
    handlePointerDown(e, id, xPct, yPct);
  };

  const onResizePointerDown = (e, id, widthPct, heightPct) => {
    setSelectedId(id);
    handleResizePointerDown(e, id, widthPct, heightPct);
  };

  const selectedItem = placeholders.find((p) => p.id === selectedId);

  const updatePlaceholderMm = (id, key, mmValue, totalMm, maxPct) => {
    const pct = ((Number(mmValue) || 0) / totalMm) * 100;
    updatePlaceholder(id, key, Math.min(Math.max(pct, 0), maxPct));
  };

  const toMm = (pct, totalMm) => Number(((pct / 100) * totalMm).toFixed(1));

  const exportData = {
    documentSize: `${docFormat.widthMm}mm x ${docFormat.heightMm}mm`,
    hasBackground: !!bgImage,
    fields: placeholders.map((p) => ({
      ...p,
      xPct: p.xPct.toFixed(2),
      yPct: p.yPct.toFixed(2),
      widthPct: p.widthPct.toFixed(2),
      heightPct: p.heightPct.toFixed(2),
    })),
  };

  // Convert the array of objects into a dictionary map for easy label lookup
  const PLACEHOLDER_LABELS = availablePlaceholders.reduce((acc, current) => {
    acc[current.value] = current.label;
    return acc;
  }, {});

  return (
    <Group
      p={0}
      m={0}
      style={{
        height: "calc(100vh - 100px - var(--mantine-spacing-md) * 2)",
        overflow: "hidden",
      }}
      align="stretch"
      wrap="nowrap"
    >
      {/* SIDEBAR */}
      <Paper
        w={350}
        p="md"
        shadow="sm"
        radius={0}
        style={{
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          overflowY: "auto",
        }}
      >
        {!selectedItem ? (
          <>
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Document Size (MM)
              </Text>

              <Group gap="xs">
                {[
                  { label: "A4", w: 210, h: 297 },
                  { label: "A5", w: 148, h: 210 },
                  { label: "A6", w: 105, h: 148 },
                  { label: "CR80", w: 54, h: 86 },
                  { label: "Letter", w: 216, h: 279 },
                ].map((preset) => {
                  const isActive =
                    docFormat.widthMm === preset.w &&
                    docFormat.heightMm === preset.h;
                  return (
                    <Badge
                      key={preset.label}
                      size="lg"
                      variant={isActive ? "filled" : "outline"}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setDocFormat({ widthMm: preset.w, heightMm: preset.h })
                      }
                    >
                      {preset.label}
                    </Badge>
                  );
                })}
              </Group>

              <Group grow>
                <NumberInput
                  label="Width"
                  value={docFormat.widthMm}
                  onChange={(val) =>
                    setDocFormat((prev) => ({ ...prev, widthMm: val || 210 }))
                  }
                  min={50}
                  max={1000}
                />
                <NumberInput
                  label="Height"
                  value={docFormat.heightMm}
                  onChange={(val) =>
                    setDocFormat((prev) => ({ ...prev, heightMm: val || 297 }))
                  }
                  min={50}
                  max={1000}
                />
              </Group>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Background
              </Text>
              <FileInput
                placeholder="Upload image"
                accept="image/png,image/jpeg"
                leftSection={<IconUpload size={16} />}
                value={bgFile}
                onChange={handleBgUpload}
                clearable
                error={bgError}
              />
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={600}>
                New Element
              </Text>
              <Group gap="xs" align="flex-end">
                <Select
                  placeholder="Select placeholder"
                  value={newPlaceholder}
                  onChange={setNewPlaceholder}
                  data={availablePlaceholders}
                  style={{ flex: 1 }}
                />
                <Button
                  onClick={handleAddPlaceholder}
                  disabled={!newPlaceholder}
                >
                  Add
                </Button>
              </Group>
            </Stack>

            {placeholders.length > 0 && (
              <Stack gap="xs" mt="sm">
                <Text size="sm" fw={600}>
                  Added Elements
                </Text>
                <Stack gap={4}>
                  {placeholders.map((p) => (
                    <Paper
                      key={p.id}
                      withBorder
                      p="xs"
                      radius="md"
                      onClick={() => setSelectedId(p.id)}
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedId === p.id ? "#e7f5ff" : "white",
                      }}
                    >
                      <Group justify="space-between">
                        <Text size="sm" fw={500}>
                          {PLACEHOLDER_LABELS[p.name] || p.name}
                        </Text>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={(e) => removePlaceholder(p.id, e)}
                          size="sm"
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            )}
          </>
        ) : (
          <Stack
            gap="xs"
            p="sm"
            style={{
              border: "1px solid #e9ecef",
              borderRadius: "8px",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Group gap="xs">
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setSelectedId(null)}
              >
                <IconArrowLeft size={16} />
              </ActionIcon>
              <Text size="sm" fw={700} c="blue">
                Edit Element
              </Text>
            </Group>

            <Select
              label="Placeholder Type"
              value={selectedItem.name}
              onChange={(val) =>
                updatePlaceholder(selectedItem.id, "name", val)
              }
              data={availablePlaceholders}
            />

            <Text size="xs" fw={600} c="dimmed" mt="xs">
              Position &amp; Size (mm)
            </Text>

            <Group grow>
              <NumberInput
                label="From left"
                value={toMm(selectedItem.xPct, docFormat.widthMm)}
                onChange={(val) =>
                  updatePlaceholderMm(
                    selectedItem.id,
                    "xPct",
                    val,
                    docFormat.widthMm,
                    100 - selectedItem.widthPct
                  )
                }
                min={0}
                max={docFormat.widthMm}
                step={0.5}
                decimalScale={1}
              />
              <NumberInput
                label="From top"
                value={toMm(selectedItem.yPct, docFormat.heightMm)}
                onChange={(val) =>
                  updatePlaceholderMm(
                    selectedItem.id,
                    "yPct",
                    val,
                    docFormat.heightMm,
                    100 - selectedItem.heightPct
                  )
                }
                min={0}
                max={docFormat.heightMm}
                step={0.5}
                decimalScale={1}
              />
            </Group>

            <Group grow>
              <NumberInput
                label="Width"
                value={toMm(selectedItem.widthPct, docFormat.widthMm)}
                onChange={(val) =>
                  updatePlaceholderMm(
                    selectedItem.id,
                    "widthPct",
                    val,
                    docFormat.widthMm,
                    100 - selectedItem.xPct
                  )
                }
                min={0}
                max={docFormat.widthMm}
                step={0.5}
                decimalScale={1}
              />
              <NumberInput
                label="Height"
                value={toMm(selectedItem.heightPct, docFormat.heightMm)}
                onChange={(val) =>
                  updatePlaceholderMm(
                    selectedItem.id,
                    "heightPct",
                    val,
                    docFormat.heightMm,
                    100 - selectedItem.yPct
                  )
                }
                min={0}
                max={docFormat.heightMm}
                step={0.5}
                decimalScale={1}
              />
            </Group>

            <Group grow>
              <NumberInput
                label="Font Size (px)"
                value={selectedItem.fontSize}
                onChange={(val) =>
                  updatePlaceholder(selectedItem.id, "fontSize", val || 12)
                }
              />
              <ColorInput
                label="Color"
                value={selectedItem.color}
                onChange={(val) =>
                  updatePlaceholder(selectedItem.id, "color", val)
                }
              />
            </Group>

            <Select
              label="Text Alignment"
              data={["left", "center", "right"]}
              value={selectedItem.align}
              onChange={(val) =>
                updatePlaceholder(selectedItem.id, "align", val)
              }
            />

            <Alert
              variant="light"
              color="blue"
              icon={<IconInfoCircle size={16} />}
              p="xs"
            >
              <Text size="xs">
                If the text is too long, the font size shrinks automatically to
                fit, down to 70% of the selected font size. Anything longer than
                that overflows horizontally based on the selected alignment.
              </Text>
            </Alert>

            <Group grow mt="xs">
              <Button
                color="red"
                variant="light"
                leftSection={<IconTrash size={16} />}
                onClick={() => removePlaceholder(selectedItem.id)}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        )}

        <Box mt="auto">
          {onSubmit || onCancel ? (
            <Group grow>
              {onCancel && (
                <Button variant="light" color="gray" onClick={handleCancel}>
                  Cancel
                </Button>
              )}
              {onSubmit && (
                <Button
                  color="teal"
                  onClick={() =>
                    onSubmit({ docFormat, bgImage, bgFile, placeholders })
                  }
                >
                  Save Template
                </Button>
              )}
            </Group>
          ) : (
            <Button fullWidth color="teal" onClick={open}>
              Preview Output JSON
            </Button>
          )}
        </Box>
      </Paper>

      {/* PREVIEW CONTAINER */}
      <Box
        ref={containerRef}
        onPointerDown={(e) => {
          if (e.target === containerRef.current || e.target === paneRef.current)
            setSelectedId(null);
        }}
        style={{
          flex: 1,
          position: "relative",
          backgroundColor: "#e9ecef",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Box
          ref={paneRef}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{
            width: `${nativeWidth}px`,
            height: `${nativeHeight}px`,
            backgroundColor: "white",
            backgroundImage: bgImage ? `url(${bgImage})` : "none",
            backgroundSize: "100% 100%",
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            transform: `scale(${scale})`,
            transformOrigin: "center center",
            position: "relative",
          }}
        >
          {placeholders.map((p) => {
            const isSelected = selectedId === p.id;
            return (
              <div
                key={p.id}
                onPointerDown={(e) =>
                  onPlaceholderPointerDown(e, p.id, p.xPct, p.yPct)
                }
                style={{
                  position: "absolute",
                  left: `${p.xPct}%`,
                  top: `${p.yPct}%`,
                  width: `${p.widthPct}%`,
                  height: `${p.heightPct}%`,
                  backgroundColor: isSelected
                    ? "rgba(51, 154, 240, 0.1)"
                    : "transparent",
                  border: isSelected
                    ? "2px dashed #339af0"
                    : "1px dashed transparent",
                  fontFamily: "Montserrat, sans-serif",
                  color: p.color,
                  fontSize: `${p.fontSize}px`,
                  textAlign: p.align,
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    p.align === "center"
                      ? "center"
                      : p.align === "right"
                        ? "flex-end"
                        : "flex-start",
                  cursor: draggingId.current === p.id ? "grabbing" : "grab",
                  userSelect: "none",
                  touchAction: "none",
                  zIndex: isSelected ? 10 : 1,
                  overflow: "hidden",
                  padding: "4px", // Little padding for text bleeding
                }}
              >
                {PLACEHOLDER_LABELS[p.name] || `{{${p.name}}}`}

                {isSelected && (
                  <ResizeHandle
                    onPointerDown={(e) =>
                      onResizePointerDown(e, p.id, p.widthPct, p.heightPct)
                    }
                  />
                )}
              </div>
            );
          })}
        </Box>
      </Box>

      <Modal
        opened={opened}
        onClose={close}
        title="Exported WYSIWYG Data"
        size="lg"
      >
        <Text size="sm" c="dimmed" mb="md">
          Send this to your backend generator.
        </Text>
        <Code block>{JSON.stringify(exportData, null, 2)}</Code>
      </Modal>
    </Group>
  );
}
